import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Trophy, ShieldCheck, Download, Plus, Pencil, Check, X } from "lucide-react";
import { formatINR, formatCr, resolveImg } from "../lib/format";
import { StatusPill } from "./AccountShell";
import { TagPicker } from "./TagPicker";
import { BuyerQuotationDraftDrawer } from "./AdminBuyerQuotations";
import { PriceLedgerAutoUpdate } from "./PriceLedgerAutoUpdate";

const CLAIM_STATUSES = ["submitted", "under_review", "approved", "rejected", "paid"];
const QUOTE_STATUSES = ["submitted", "under_review", "accepted", "rejected"];

const StatusSelect = ({ value, options, onChange, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-transparent border border-white/20 rounded-full px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#C9C9C2] focus:border-[#B87333] outline-none [&>option]:bg-[#0e0e0e] [&>option]:text-[#F5F5F0]"
  >
    {options.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
  </select>
);

export function Claims({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/claims")).data); }
    catch { toast.error("Could not load claims."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/claims/${id}/status`, { status }); toast.success(`Claim marked ${status.replace(/_/g, " ")} · buyer emailed`); load(); }
    catch { toast.error("Could not update claim."); }
  };

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]" data-testid="no-admin-claims">No commission claims submitted yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-claims-list">
      {items.map((c) => (
        <div key={c.id} data-testid="admin-claim-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{c.kind} order</span>
                <span className="font-serif text-2xl">{c.order_ref}</span>
                <StatusPill status={c.status} />
              </div>
              <div className="font-body text-sm text-[#8A8A82] mt-2">{c.buyer_email}{c.buyer_category ? ` · ${c.buyer_category}` : ""}</div>
            </div>
            <div className="text-right">
              <div className="font-serif text-2xl text-[#2FB999]">{formatINR(c.amount_claimed)}</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">{new Date(c.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
            <Cell l="Order value" v={formatINR(c.order_value)} />
            <Cell l="Material" v={`${c.material}${c.quantity ? ` · ${c.quantity}` : ""}`} />
            <Cell l="Rate" v={`${c.rate_pct}%`} />
            {c.order_date && <Cell l="Order date" v={c.order_date} />}
            {c.kind === "indirect" && c.end_customer && <Cell l="End-customer" v={c.end_customer} />}
            {c.kind === "indirect" && c.deal_role && <Cell l="Role" v={c.deal_role} />}
            <Cell l="Payout details" v={c.payout_details} full />
            {c.remarks && <Cell l="Remarks" v={c.remarks} full />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <StatusSelect testid={`claim-status-select-${c.id}`} value={c.status} options={CLAIM_STATUSES} onChange={(s) => setStatus(c.id, s)} />
            {c.document_url && (
              <a data-testid={`claim-doc-${c.id}`} href={resolveImg(c.document_url)} target="_blank" rel="noreferrer" className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">View document ↗</a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Quotations({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/quotations")).data); }
    catch { toast.error("Could not load quotations."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotations/${id}/status`, { status }); toast.success(`Quotation marked ${status.replace(/_/g, " ")} · seller emailed`); load(); }
    catch { toast.error("Could not update quotation."); }
  };

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]" data-testid="no-admin-quotations">No seller quotations submitted yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-quotations-list">
      {items.map((q) => (
        <div key={q.id} data-testid="admin-quotation-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-serif text-2xl">{q.rfq_ref}</span>
                <StatusPill status={q.status} />
              </div>
              <div className="font-body text-sm text-[#8A8A82] mt-2">{q.seller_business || q.seller_email} · {q.seller_email}</div>
            </div>
            <div className="text-right">
              <div className="font-serif text-2xl text-[#2FB999]">{formatINR(q.total)}</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">{new Date(q.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
            <Cell l="Material" v={`${q.material}${q.grade ? ` · ${q.grade}` : ""}`} />
            <Cell l="Quantity" v={`${q.quantity} ${q.unit || ""}`} />
            <Cell l="Unit price" v={formatINR(q.unit_price)} />
            <Cell l="Delivery" v={q.delivery_timeline} />
            {q.validity && <Cell l="Validity" v={q.validity} />}
            {q.payment_terms && <Cell l="Payment terms" v={q.payment_terms} />}
            {q.remarks && <Cell l="Remarks" v={q.remarks} full />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <StatusSelect testid={`quote-status-select-${q.id}`} value={q.status} options={QUOTE_STATUSES} onChange={(s) => setStatus(q.id, s)} />
            {q.document_url && (
              <a data-testid={`quote-doc-${q.id}`} href={resolveImg(q.document_url)} target="_blank" rel="noreferrer" className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">View document ↗</a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const QUOTE_REQ_STATUSES = ["open", "contacted", "quoted", "closed"];

function ConvertPanel({ api, qr, onDone }) {
  const [data, setData] = useState(null);
  const [pid, setPid] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/quote-requests/${qr.id}/match`).then((r) => {
      setData(r.data);
      const p = r.data.product;
      setPid(p ? p.id : "");
      setPrice(p ? p.price : "");
      setQty(r.data.quantity || 1);
    }).catch(() => setData({ candidates: [], product: null, quantity: 1 }));
  }, [api, qr.id]);

  const onPick = (id) => {
    setPid(id);
    const p = (data?.candidates || []).find((c) => c.id === id);
    if (p) setPrice(p.price);
  };

  const convert = async () => {
    if (!pid) { toast.error("Select a product to convert."); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post(`/quote-requests/${qr.id}/convert`, {
        product_id: pid, unit_price: parseFloat(price) || undefined, quantity: parseInt(qty, 10) || 1,
      });
      toast.success(`Order ${res.order.id.slice(0, 8).toUpperCase()} created · buyer notified`);
      onDone();
    } catch { toast.error("Could not create the order."); }
    finally { setSaving(false); }
  };

  if (data === null) return <p className="font-body text-xs text-[#8A8A82] mt-4">Matching catalogue products…</p>;
  const cands = data.candidates || [];
  return (
    <div className="mt-5 border-t border-white/10 pt-5" data-testid={`convert-panel-${qr.id}`}>
      {cands.length === 0 ? (
        <p className="font-body text-sm text-[#8A8A82]">No matching in-stock product found. Update the catalogue or contact the buyer manually.</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <label className="block sm:col-span-1">
            <span className="overline text-[#8A8A82]">Brand / product</span>
            <select data-testid={`convert-product-${qr.id}`} value={pid} onChange={(e) => onPick(e.target.value)} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2.5 mt-2 font-body text-[#F5F5F0] [&>option]:bg-[#0e0e0e]">
              {cands.map((c) => <option key={c.id} value={c.id}>{c.name} — ₹{c.price}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="overline text-[#8A8A82]">Unit price (₹)</span>
            <input data-testid={`convert-price-${qr.id}`} type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2.5 mt-2 font-body text-[#F5F5F0]" />
          </label>
          <label className="block">
            <span className="overline text-[#8A8A82]">Quantity</span>
            <input data-testid={`convert-qty-${qr.id}`} type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2.5 mt-2 font-body text-[#F5F5F0]" />
          </label>
          <div className="sm:col-span-3">
            <button data-testid={`convert-submit-${qr.id}`} onClick={convert} disabled={saving} className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{saving ? "Creating order…" : "Create order & invoice"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function QuoteRequests({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertId, setConvertId] = useState(null);
  const [draftQuotationId, setDraftQuotationId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/quote-requests")).data); }
    catch { toast.error("Could not load quote requests."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/quote-requests/${id}/status`, { status }); toast.success(`Request marked ${status}`); load(); }
    catch { toast.error("Could not update request."); }
  };

  const downloadProforma = async (orderId) => {
    try {
      const res = await api.get(`/orders/${orderId}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Vinci-Proforma-${orderId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download proforma."); }
  };

  if (loading) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  if (items.length === 0) return <p className="font-body text-[#8A8A82]" data-testid="no-quote-requests">No buyer quote requests yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-quote-requests-list">
      {items.map((q) => (
        <div key={q.id} data-testid="admin-quote-request-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{q.ref}</span>
                <span className="font-serif text-2xl">{q.material}</span>
                <StatusPill status={q.status} />
              </div>
              <div className="font-body text-sm text-[#8A8A82] mt-2">{q.name}{q.company ? ` · ${q.company}` : ""} · {q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
            </div>
            <div className="font-body text-xs text-[#8A8A82]">{new Date(q.created_at).toLocaleString()}</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
            <Cell l="Quantity" v={`${q.quantity} ${q.unit || ""}`} />
            {q.weight_tons != null && <Cell l="Est. weight" v={`${q.weight_tons} Metric Tons`} />}
            {q.num_bars && <Cell l="Bars" v={`${q.num_bars} × ${q.length_per_bar || "?"} m`} />}
            {q.grade && <Cell l="Grade" v={q.grade} />}
            {q.diameter && <Cell l="Diameter" v={`⌀ ${q.diameter} mm`} />}
            {q.delivery_state && <Cell l="Delivery state" v={q.delivery_state} />}
            {q.notes && <Cell l="Notes" v={q.notes} full />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <StatusSelect testid={`quote-request-status-${q.id}`} value={q.status} options={QUOTE_REQ_STATUSES} onChange={(s) => setStatus(q.id, s)} />
            {q.order_id ? (
              <button data-testid={`quote-request-proforma-${q.id}`} onClick={() => downloadProforma(q.order_id)} className="rounded-full border border-[#2FB999]/50 text-[#2FB999] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#2FB999]/10 transition-colors">Download proforma ↓</button>
            ) : (
              <>
                <button data-testid={`quote-request-draft-quotation-${q.id}`}
                        onClick={() => setDraftQuotationId(draftQuotationId === q.id ? null : q.id)}
                        className="rounded-full border border-[#B87333]/50 text-[#B87333] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333]/10 transition-colors">
                  {draftQuotationId === q.id ? "Close draft" : "Draft quotation"}
                </button>
                <button data-testid={`quote-request-convert-${q.id}`} onClick={() => setConvertId(convertId === q.id ? null : q.id)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">{convertId === q.id ? "Cancel" : "Convert to order"}</button>
              </>
            )}
            <a data-testid={`quote-request-mail-${q.id}`} href={`mailto:${q.email}?subject=Your Vinci quote request ${q.ref}`} className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">Email buyer ↗</a>
          </div>
          {draftQuotationId === q.id && <BuyerQuotationDraftDrawer api={api} qr={q} onDone={() => { setDraftQuotationId(null); load(); }} />}
          {convertId === q.id && !q.order_id && <ConvertPanel api={api} qr={q} onDone={() => { setConvertId(null); load(); }} />}
        </div>
      ))}
    </div>
  );
}

const EMPTY_ENTITY = { state: "", entity_name: "", entity_type: "", gstin: "", cin: "", address: "", phone: "", email: "" };

export function RewardsLeaderboard({ api }) {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("annual");
  useEffect(() => {
    api.get("/rewards/leaderboard").then((r) => setData(r.data)).catch(() => toast.error("Could not load rewards leaderboard."));
  }, [api]);

  if (!data) return <p className="font-body text-[#8A8A82]">Loading…</p>;
  const partners = [...data.partners].sort((a, b) => (b[`${scope}_achieved`] - a[`${scope}_achieved`]));

  return (
    <div data-testid="admin-rewards">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="font-body text-sm text-[#8A8A82] max-w-xl">Selling-partner turnover vs targets. Achievers unlock the reward ladder.</p>
        <div className="flex items-center gap-2 rounded-full border border-white/15 p-1">
          {["monthly", "annual"].map((s) => (
            <button key={s} data-testid={`rewards-scope-${s}`} onClick={() => setScope(s)}
              className={`px-4 py-2 rounded-full font-grotesk text-[10px] uppercase tracking-[0.16em] transition-colors ${scope === s ? "bg-[#B87333] text-black" : "text-[#C9C9C2] hover:text-[#F5F5F0]"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border border-white/10 rounded-sm">
        <table className="w-full text-left" data-testid="rewards-table">
          <thead>
            <tr className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] border-b border-white/10">
              <th className="p-4">#</th><th className="p-4">Partner</th><th className="p-4">Tier</th>
              <th className="p-4 text-right">Achieved</th><th className="p-4 text-right">Target</th>
              <th className="p-4 text-right">%</th><th className="p-4 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p, i) => {
              const ach = p[`${scope}_achiever`];
              return (
                <tr key={p.email} data-testid="rewards-row" className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 font-serif text-lg text-[#B87333]">{i + 1}</td>
                  <td className="p-4"><div className="font-body text-[#F5F5F0]">{p.name}</div><div className="font-body text-xs text-[#8A8A82]">{p.email}{p.top_state ? ` · ${p.top_state}` : ""}</div></td>
                  <td className="p-4 font-body text-sm text-[#C9C9C2]">{p.tier_label}<span className="text-[10px] text-[#8A8A82]"> · {p.state_group}</span></td>
                  <td className="p-4 text-right font-serif">{formatCr(p[`${scope}_achieved`])}</td>
                  <td className="p-4 text-right font-body text-sm text-[#8A8A82]">{formatCr(p[`${scope}_target`])}</td>
                  <td className="p-4 text-right font-body text-sm" style={{ color: ach ? "#2FB999" : "#C9C9C2" }}>{p[`${scope}_pct`]}%</td>
                  <td className="p-4 text-center">
                    {ach ? <span className="inline-flex items-center gap-1 font-grotesk text-[9px] uppercase tracking-[0.16em] text-[#2FB999]"><Trophy size={11} /> Achiever</span>
                         : <span className="font-grotesk text-[9px] uppercase tracking-[0.16em] text-[#6f6f68]">In progress</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#B87333] mt-10 mb-4">Reward ladder</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {data.ladder.map((r) => (
          <div key={r.key} className="rounded-sm border border-white/10 p-6 bg-[#0a0a0a]">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82]">{r.level}</div>
            <div className="font-serif text-xl mt-1">{r.title}</div>
            <ul className="mt-3 space-y-1.5">
              {r.prizes.map((pr) => <li key={pr} className="font-body text-sm text-[#C9C9C2] flex gap-2"><span className="text-[#B87333]">·</span>{pr}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BillingEntities({ api }) {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [form, setForm] = useState(EMPTY_ENTITY);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setItems((await api.get("/billing-entities")).data); } catch { toast.error("Could not load billing entities."); }
  }, [api]);
  useEffect(() => { load(); api.get("/states").then((r) => setStates(r.data)).catch(() => {}); }, [load, api]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.state || !form.entity_name) { toast.error("State and entity name are required."); return; }
    setSaving(true);
    try {
      await api.post(`/billing-entities?is_default=${isDefault}`, form);
      toast.success("Billing entity saved.");
      setForm(EMPTY_ENTITY); setIsDefault(false); load();
    } catch { toast.error("Could not save entity."); }
    finally { setSaving(false); }
  };

  const edit = (ent) => { setForm({ ...EMPTY_ENTITY, ...ent }); setIsDefault(!!ent.is_default); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (state) => {
    if (!window.confirm(`Remove the billing entity mapped to ${state}? Orders shipping there will fall back to the default entity.`)) return;
    try { await api.delete(`/billing-entities/${encodeURIComponent(state)}`); toast.success("Entity removed."); load(); }
    catch { toast.error("Could not remove entity."); }
  };

  return (
    <div data-testid="admin-billing-entities">
      <div className="rounded-sm border border-white/10 p-6 md:p-8 mb-8">
        <h3 className="font-serif text-2xl mb-2">Add / update a state billing entity</h3>
        <p className="font-body text-sm text-[#8A8A82] mb-6">These details are private — they appear only on the tax invoice for orders shipping to the mapped state. Mark one entity as the default fallback.</p>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-6">
          <label className="block">
            <span className="overline text-[#8A8A82]">Delivery state *</span>
            <select data-testid="entity-state" name="state" value={form.state} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] [&>option]:bg-[#0e0e0e]">
              <option value="">Select state…</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <EF label="Registered entity name *" name="entity_name" v={form.entity_name} onChange={onChange} testid="entity-name" />
          <EF label="Entity type (Pvt Ltd / LLP / Partnership / Proprietorship)" name="entity_type" v={form.entity_type} onChange={onChange} testid="entity-type" />
          <EF label="GSTIN" name="gstin" v={form.gstin} onChange={onChange} testid="entity-gstin" />
          <EF label="CIN" name="cin" v={form.cin} onChange={onChange} testid="entity-cin" />
          <EF label="Phone" name="phone" v={form.phone} onChange={onChange} testid="entity-phone" />
          <EF label="Email" name="email" v={form.email} onChange={onChange} testid="entity-email" />
          <label className="block md:col-span-2">
            <span className="overline text-[#8A8A82]">Registered address</span>
            <textarea data-testid="entity-address" name="address" rows={2} value={form.address} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] resize-none" />
          </label>
          <label className="flex items-center gap-3 md:col-span-2 cursor-pointer">
            <input data-testid="entity-default" type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="accent-[#B87333] w-4 h-4" />
            <span className="font-body text-sm text-[#C9C9C2]">Use as default billing entity (fallback when a state has no mapping)</span>
          </label>
          <div className="md:col-span-2">
            <button data-testid="entity-save" type="submit" disabled={saving} className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save entity"}</button>
          </div>
        </form>
      </div>

      {items.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="no-entities">No billing entities configured yet. Orders will bill under the default "Vinci Inc." until you add one.</p>
      ) : (
        <div className="space-y-3" data-testid="entities-list">
          {items.map((ent) => (
            <div key={ent.state} data-testid="entity-row" className="border border-white/10 rounded-sm p-5 flex flex-wrap items-start justify-between gap-4 hover:border-white/20 transition-colors">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-serif text-xl">{ent.entity_name}</span>
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{ent.state === "__default__" ? "All states" : ent.state}</span>
                  {ent.is_default && <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#2FB999] border border-[#2FB999]/40 rounded-full px-2.5 py-1">Default</span>}
                </div>
                <div className="font-body text-sm text-[#8A8A82] mt-2">
                  {ent.entity_type ? `${ent.entity_type} · ` : ""}{ent.gstin ? `GSTIN ${ent.gstin}` : "No GSTIN"}{ent.cin ? ` · CIN ${ent.cin}` : ""}
                </div>
                {ent.address && <div className="font-body text-xs text-[#6f6f68] mt-1 max-w-xl">{ent.address}</div>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`entity-edit-${ent.state}`} onClick={() => edit(ent)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Edit</button>
                <button data-testid={`entity-delete-${ent.state}`} onClick={() => remove(ent.state)} className="grid place-items-center w-9 h-9 rounded-full border border-white/20 hover:border-[#C0392B] hover:text-[#C0392B] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Cell = ({ l, v, full }) => (
  <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
    <span className="overline text-[#8A8A82]">{l}</span>
    <div className="font-body text-sm text-[#C9C9C2] mt-1">{v}</div>
  </div>
);

const EF = ({ label, name, v, onChange, testid }) => (
  <label className="block">
    <span className="overline text-[#8A8A82]">{label}</span>
    <input data-testid={testid} name={name} value={v} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0]" />
  </label>
);

function DailyPulseGraphic({ api }) {
  const [busy, setBusy] = useState(null); // "post" | "story" | "email" | "whatsapp" | null
  const [headline, setHeadline] = useState("");
  const [broadcastResult, setBroadcastResult] = useState(null);

  const q = () => {
    const p = new URLSearchParams();
    if (headline.trim()) p.set("headline", headline.trim());
    return p.toString() ? `&${p.toString()}` : "";
  };

  const download = async (size) => {
    setBusy(size);
    try {
      const res = await api.get(`/admin/graphics/daily-pulse.png?size=${size}${q()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vinci-daily-pulse-${new Date().toISOString().slice(0, 10)}-${size}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Daily Pulse ${size === "post" ? "Post" : "Story"} downloaded`);
    } catch {
      toast.error("Could not render graphic. Try again shortly.");
    } finally { setBusy(null); }
  };

  const broadcastEmail = async () => {
    if (!window.confirm("Email today's Daily Pulse graphic to every active partner? This can take a few seconds.")) return;
    setBusy("email");
    setBroadcastResult(null);
    try {
      const res = await api.post(`/admin/graphics/broadcast-daily-pulse?${q().slice(1)}`);
      setBroadcastResult(res.data);
      toast.success(`Sent to ${res.data.emailed} partners (${res.data.failed} failed).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(null); }
  };

  const copyWhatsapp = async () => {
    setBusy("whatsapp");
    try {
      const res = await api.get("/admin/graphics/whatsapp-broadcast-text");
      const text = res.data?.text || "";
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste into your WhatsApp Business broadcast list.");
    } catch {
      toast.error("Could not copy WhatsApp text");
    } finally { setBusy(null); }
  };

  const openWhatsappWeb = async () => {
    try {
      const res = await api.get("/admin/graphics/whatsapp-broadcast-text");
      const text = encodeURIComponent(res.data?.text || "");
      window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
    } catch { toast.error("Could not fetch WhatsApp text"); }
  };

  return (
    <div
      data-testid="daily-pulse-graphic"
      className="mb-8 rounded-sm border border-[#D8B23E]/40 bg-gradient-to-r from-[#D8B23E]/[0.06] to-transparent px-5 py-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]">
            Marketing · Branded Graphic
          </div>
          <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
            Generate today's Daily Pulse image
          </div>
          <div className="font-body text-xs text-[#8A8A82] mt-1">
            Obsidian/gold PNG auto-populated with live rates, VINCI INC. wordmark and Veni · Vidi · Vici footer.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="daily-pulse-post" disabled={busy !== null} onClick={() => download("post")}
                  className="rounded-full bg-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:brightness-110 disabled:opacity-50">
            {busy === "post" ? "Rendering…" : "Insta Post · 1080×1350"}
          </button>
          <button data-testid="daily-pulse-story" disabled={busy !== null} onClick={() => download("story")}
                  className="rounded-full border border-[#D8B23E]/60 hover:border-[#D8B23E] text-[#D8B23E] px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-50">
            {busy === "story" ? "Rendering…" : "Reels · 1080×1920"}
          </button>
        </div>
      </div>

      {/* Custom headline + distribution row */}
      <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
        <input
          data-testid="daily-pulse-headline"
          type="text"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          maxLength={90}
          placeholder="Custom headline (optional) — e.g. Independence Day · Rates Held Steady"
          className="flex-1 min-w-[240px] bg-transparent border-b border-white/20 focus:border-[#D8B23E] outline-none py-2 font-body text-sm text-[#F5F5F0] placeholder:text-[#5f5f58]"
        />
        <button data-testid="daily-pulse-broadcast" disabled={busy !== null} onClick={broadcastEmail}
                className="rounded-full border border-[#2FB999]/60 hover:border-[#2FB999] text-[#2FB999] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-50">
          {busy === "email" ? "Broadcasting…" : "📧 Email all partners"}
        </button>
        <button data-testid="daily-pulse-whatsapp-copy" disabled={busy !== null} onClick={copyWhatsapp}
                className="rounded-full border border-white/25 hover:border-[#25D366] text-[#C9C9C2] hover:text-[#25D366] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-50">
          {busy === "whatsapp" ? "Copying…" : "📋 Copy WhatsApp text"}
        </button>
        <button data-testid="daily-pulse-whatsapp-open" onClick={openWhatsappWeb}
                className="rounded-full border border-white/25 hover:border-[#25D366] text-[#C9C9C2] hover:text-[#25D366] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          Open WhatsApp
        </button>
      </div>

      {broadcastResult && (
        <div data-testid="broadcast-result" className="mt-3 text-[11px] text-[#8A8A82] font-grotesk uppercase tracking-[0.18em]">
          Emailed · <span className="text-[#2FB999]">{broadcastResult.emailed}</span>   ·   Failed · <span className="text-[#C0392B]">{broadcastResult.failed}</span>   ·   With phone on file · <span className="text-[#D8B23E]">{broadcastResult.with_phone}</span>
        </div>
      )}
    </div>
  );
}


function VerifiedPartnerGraphic({ api }) {
  const [partners, setPartners] = useState([]);
  const [uid, setUid] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/graphics/recent-partners").then((r) => {
      const list = r.data?.partners || [];
      setPartners(list);
      if (list.length && !uid) setUid(list[0].uid);
    }).catch(() => setPartners([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const download = async (size) => {
    if (!uid) { toast.error("Pick a partner"); return; }
    setBusy(size);
    try {
      const res = await api.get(`/admin/graphics/verified-partner.png?uid=${uid}&size=${size}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vinci-verified-partner-${size}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Verified Partner graphic downloaded");
    } catch {
      toast.error("Could not render partner graphic");
    } finally { setBusy(null); }
  };

  return (
    <div
      data-testid="verified-partner-graphic"
      className="mb-8 rounded-sm border border-[#B87333]/40 bg-gradient-to-r from-[#B87333]/[0.06] to-transparent px-5 py-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#B87333]">
            Marketing · Verified Partner Card
          </div>
          <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
            Celebrate a new Vinci partner
          </div>
          <div className="font-body text-xs text-[#8A8A82] mt-1">
            Central copper monogram medallion carrying the partner's Vinci Code plus business name, state and category.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="verified-partner-select"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            className="bg-[#0a0a0a] border border-white/20 rounded-full text-[#F5F5F0] text-xs font-body px-4 py-2 focus:border-[#B87333] outline-none"
          >
            <option value="" disabled>Pick a partner…</option>
            {partners.map((p) => (
              <option key={p.uid} value={p.uid}>{p.vinci_code} · {p.label}</option>
            ))}
          </select>
          <button data-testid="verified-partner-post" disabled={busy !== null || !uid} onClick={() => download("post")}
                  className="rounded-full bg-[#B87333] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:brightness-110 disabled:opacity-50">
            {busy === "post" ? "Rendering…" : "Insta Post"}
          </button>
          <button data-testid="verified-partner-story" disabled={busy !== null || !uid} onClick={() => download("story")}
                  className="rounded-full border border-[#B87333]/60 hover:border-[#B87333] text-[#B87333] px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] disabled:opacity-50">
            {busy === "story" ? "Rendering…" : "Reels"}
          </button>
        </div>
      </div>
    </div>
  );
}


export function DailyRates({ api }) {
  const [rates, setRates] = useState([]);
  const [edits, setEdits] = useState({});
  const load = useCallback(async () => {
    try { setRates((await api.get("/rates")).data); } catch { toast.error("Could not load rates."); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const downloadTemplate = (kind, ext) => {
    api.get(`/admin/price-pulse/template/${kind}.${ext}`, { responseType: "blob" })
      .then((r) => {
        const blobUrl = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `Vinci-Master-${kind === "cement" ? "Cement" : "Steel"}-Template.${ext}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
      })
      .catch(() => toast.error("Template download failed"));
  };

  const save = async (r) => {
    const price = parseFloat(edits[r.category]);
    if (!price || price <= 0) { toast.error("Enter a valid price."); return; }
    try {
      await api.post("/rates", { category: r.category, label: r.label, unit: r.unit, price });
      toast.success(`${r.label} rate updated · flashed to buyers`);
      setEdits((e) => ({ ...e, [r.category]: "" }));
      load();
    } catch { toast.error("Could not update rate."); }
  };

  return (
    <div data-testid="admin-rates">
      <p className="font-body text-sm text-[#8A8A82] mb-6">Update today's indicative material rates. The change vs. the previous rate is auto-computed and shown as an up/down flash in the store ticker.</p>

      {/* Vinci Master Templates — one-tap CSV / XLSX download for the
          Bulk Price Updater. Every column here maps 1:1 to what the
          uploader expects. */}
      <div className="mb-6 border border-[#D8B23E]/40 rounded-sm p-5 bg-gradient-to-b from-[#0B0B0B] to-[#050505]"
           data-testid="daily-rates-templates">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333]">Vinci Master Template</div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">Download the Bulk Price Updater templates</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">
              Fill · Save · Upload above. Missing GST / per-MT / per-bar columns are auto-computed. XLSX bundles a Cheat Sheet tab with the exact formulae.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => downloadTemplate("cement", "xlsx")}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] bg-[#D8B23E] text-[#0A0A0A] hover:bg-[#c8a336] rounded-sm"
              data-testid="daily-rates-template-cement-xlsx">Download Cement Template (XLSX)</button>
            <button type="button" onClick={() => downloadTemplate("cement", "csv")}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]/60 rounded-sm"
              data-testid="daily-rates-template-cement-csv">CSV</button>
            <button type="button" onClick={() => downloadTemplate("steel", "xlsx")}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] bg-[#D8B23E] text-[#0A0A0A] hover:bg-[#c8a336] rounded-sm"
              data-testid="daily-rates-template-tmt-xlsx">Download TMT Template (XLSX)</button>
            <button type="button" onClick={() => downloadTemplate("steel", "csv")}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]/60 rounded-sm"
              data-testid="daily-rates-template-tmt-csv">CSV</button>
          </div>
        </div>
      </div>

      <DailyPulseGraphic api={api} />
      <VerifiedPartnerGraphic api={api} />
      <PriceLedgerAutoUpdate api={api} />

      <div className="space-y-3">
        {rates.map((r) => (
          <div key={r.category} data-testid={`rate-row-${r.category}`} className="border border-white/10 rounded-sm p-5 flex flex-wrap items-center gap-4 hover:border-white/20 transition-colors">
            <div className="min-w-[180px]">
              <div className="font-serif text-lg">{r.label}</div>
              <div className="font-body text-xs text-[#8A8A82]">{r.unit}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-body text-sm text-[#8A8A82]">Current</span>
              <span className="font-serif text-xl">{formatINR(r.price)}</span>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.14em]" style={{ color: r.trend === "up" ? "#2FB999" : r.trend === "down" ? "#C0392B" : "#8A8A82" }}>
                {r.change_pct > 0 ? "+" : ""}{r.change_pct}%
              </span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <input
                data-testid={`rate-input-${r.category}`}
                type="number"
                placeholder="New price"
                value={edits[r.category] ?? ""}
                onChange={(e) => setEdits((x) => ({ ...x, [r.category]: e.target.value }))}
                className="w-32 bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-2 font-body text-base text-[#F5F5F0]"
              />
              <button data-testid={`rate-save-${r.category}`} onClick={() => save(r)} className="rounded-full bg-[#F5F5F0] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">Update</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_RFQ = { title: "", material: "", grade: "", quantity: "", unit: "", destination_state: "", target_delivery: "", notes: "" };

function RfqQuotes({ api, rid }) {
  const [quotes, setQuotes] = useState(null);
  useEffect(() => { api.get(`/rfqs/${rid}/quotations`).then((r) => setQuotes(r.data)).catch(() => setQuotes([])); }, [api, rid]);
  if (quotes === null) return <p className="font-body text-xs text-[#8A8A82] mt-3">Loading quotes…</p>;
  if (quotes.length === 0) return <p className="font-body text-xs text-[#8A8A82] mt-3">No quotations received yet.</p>;
  return (
    <div className="mt-4 space-y-2 border-t border-white/10 pt-4" data-testid={`rfq-quotes-${rid}`}>
      {quotes.map((q) => (
        <div key={q.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-body text-[#C9C9C2]">{q.seller_business || q.seller_email}</span>
          <span className="font-body text-[#8A8A82]">{q.quantity} {q.unit} @ {formatINR(q.unit_price)} · <span className="text-[#2FB999]">{formatINR(q.total)}</span></span>
          <StatusPill status={q.status} />
        </div>
      ))}
    </div>
  );
}

export function RFQs({ api }) {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [form, setForm] = useState(EMPTY_RFQ);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/rfqs")).data); } catch { toast.error("Could not load requirements."); }
  }, [api]);
  useEffect(() => {
    load();
    api.get("/states").then((r) => setStates(r.data)).catch(() => {});
    api.get("/material-tags").then((r) => setTags(r.data)).catch(() => {});
  }, [load, api]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggleTag = (t) => setSelectedTags((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const post = async (e) => {
    e.preventDefault();
    if (!form.title || !form.material || !form.quantity) { toast.error("Title, material and quantity are required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/rfqs", { ...form, material_tags: selectedTags });
      toast.success(`Requirement ${data.ref} posted · ${data.invited || 0} matching seller(s) invited`);
      setForm(EMPTY_RFQ); setSelectedTags([]); load();
    } catch { toast.error("Could not post requirement."); }
    finally { setSaving(false); }
  };

  const close = async (id) => {
    try { await api.patch(`/rfqs/${id}/close`); toast.success("Requirement closed."); load(); }
    catch { toast.error("Could not close requirement."); }
  };

  return (
    <div data-testid="admin-rfqs">
      <div className="rounded-sm border border-white/10 p-6 md:p-8 mb-8">
        <h3 className="font-serif text-2xl mb-2">Post a requirement</h3>
        <p className="font-body text-sm text-[#8A8A82] mb-6">Broadcast a buyer requirement to sellers. Sellers whose supplied materials match are emailed an invitation to quote; all approved sellers can see and quote open requirements.</p>
        <form onSubmit={post} className="grid md:grid-cols-2 gap-6">
          <EF label="Requirement title *" name="title" v={form.title} onChange={onChange} testid="rfq-title" />
          <EF label="Material *" name="material" v={form.material} onChange={onChange} testid="rfq-material" />
          <EF label="Grade / spec" name="grade" v={form.grade} onChange={onChange} testid="rfq-grade" />
          <EF label="Quantity *" name="quantity" v={form.quantity} onChange={onChange} testid="rfq-quantity" />
          <EF label="Unit" name="unit" v={form.unit} onChange={onChange} testid="rfq-unit" />
          <label className="block">
            <span className="overline text-[#8A8A82]">Destination state</span>
            <select data-testid="rfq-state" name="destination_state" value={form.destination_state} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] [&>option]:bg-[#0e0e0e]">
              <option value="">Any / unspecified</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <EF label="Target delivery" name="target_delivery" v={form.target_delivery} onChange={onChange} testid="rfq-delivery" />
          <div className="md:col-span-2" data-testid="rfq-tags">
            <TagPicker
              label="Match to seller categories (sellers with these tags get an email invite)"
              tags={tags}
              selected={selectedTags}
              onToggle={toggleTag}
              testidPrefix="rfq-tag"
            />
          </div>
          <label className="block md:col-span-2">
            <span className="overline text-[#8A8A82]">Notes</span>
            <textarea data-testid="rfq-notes" name="notes" rows={2} value={form.notes} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] resize-none" />
          </label>
          <div className="md:col-span-2">
            <button data-testid="rfq-post" type="submit" disabled={saving} className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{saving ? "Posting…" : "Post & invite sellers"}</button>
          </div>
        </form>
      </div>

      {items.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="no-rfqs">No requirements posted yet.</p>
      ) : (
        <div className="space-y-4" data-testid="rfqs-list">
          {items.map((r) => (
            <div key={r.id} data-testid="rfq-row" className="border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{r.ref}</span>
                    <span className="font-serif text-2xl">{r.title}</span>
                    <StatusPill status={r.status === "open" ? "under_review" : "rejected"} />
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">{r.status}</span>
                  </div>
                  <div className="font-body text-sm text-[#8A8A82] mt-2">
                    {r.material}{r.grade ? ` · ${r.grade}` : ""} · {r.quantity} {r.unit || ""}
                    {r.destination_state ? ` · ${r.destination_state}` : ""}{r.target_delivery ? ` · ${r.target_delivery}` : ""}
                  </div>
                  {r.notes && <div className="font-body text-xs text-[#6f6f68] mt-1">{r.notes}</div>}
                </div>
                <div className="text-right">
                  <div className="font-serif text-2xl text-[#2FB999]">{r.quote_count}</div>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">quotes</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                <button data-testid={`rfq-view-${r.id}`} onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">{expanded === r.id ? "Hide quotes" : "View quotes"}</button>
                {r.status === "open" && (
                  <button data-testid={`rfq-close-${r.id}`} onClick={() => close(r.id)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C0392B] hover:text-[#C0392B] transition-colors">Close requirement</button>
                )}
              </div>
              {expanded === r.id && <RfqQuotes api={api} rid={r.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ==================== KYC Verifications ====================
export function KycVerifications({ api, onCount }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [zoho, setZoho] = useState({ configured: false, credentials_present: false });
  const [zohoBusy, setZohoBusy] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteCode, setPasteCode] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      setItems((await api.get(`/kyc${q}`)).data);
      onCount?.();
    } catch { toast.error("Could not load verifications."); }
    finally { setLoading(false); }
  }, [api, filter, onCount]);
  useEffect(() => { load(); }, [load]);

  const loadZoho = useCallback(async () => {
    try { setZoho((await api.get("/kyc/zoho/config")).data); } catch { /* silent */ }
  }, [api]);
  useEffect(() => { loadZoho(); }, [loadZoho]);

  const connectZoho = async () => {
    setZohoBusy(true);
    try {
      const { data } = await api.get("/admin/kyc/zoho/consent-url");
      const w = window.open(data.consent_url, "zoho_connect", "width=680,height=780,noopener");
      // Poll config every 3s until connected or user closes
      const started = Date.now();
      const iv = setInterval(async () => {
        try {
          const c = (await api.get("/kyc/zoho/config")).data;
          if (c.configured) {
            setZoho(c); toast.success("Zoho Sign connected.");
            clearInterval(iv); setZohoBusy(false);
            try { w?.close(); } catch { /* noop */ }
          } else if (Date.now() - started > 180000) {
            clearInterval(iv); setZohoBusy(false);
          }
        } catch { /* silent */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate consent URL.");
      setZohoBusy(false);
    }
  };

  const pasteAuthCode = async () => {
    const code = pasteCode.trim();
    if (!code) { toast.error("Paste the authorization code first."); return; }
    setZohoBusy(true);
    try {
      await api.post("/admin/kyc/zoho/paste-code", { code });
      toast.success("Zoho Sign connected.");
      setPasteCode(""); setShowPaste(false);
      await loadZoho();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to exchange code.");
    } finally { setZohoBusy(false); }
  };

  const disconnectZoho = async () => {
    if (!window.confirm("Disconnect Zoho Sign? Existing signed PDFs are preserved.")) return;
    try { await api.post("/admin/kyc/zoho/disconnect"); toast.success("Disconnected."); await loadZoho(); }
    catch { toast.error("Could not disconnect."); }
  };

  const [reminding, setReminding] = useState(false);
  const sendReminders = async () => {
    if (!window.confirm("Send a reminder email to every partner who hasn't finished eKYC in the last 24h? (Rate-limited to once per 48h per partner.)")) return;
    setReminding(true);
    try {
      const { data } = await api.post("/admin/kyc/send-reminders?min_hours=24");
      toast.success(`Reminders sent: ${data.sent} · skipped (recent): ${data.skipped_recent} · failed: ${data.failed}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminders.");
    } finally { setReminding(false); }
  };

  const ack = async (uid) => {
    try { await api.post(`/kyc/${uid}/ack`); toast.success("Marked as reviewed."); load(); }
    catch { toast.error("Could not update."); }
  };

  const colors = { verified: "#2FB999", failed: "#C0392B", pending: "#D8B23E" };
  return (
    <div data-testid="admin-kyc">
      {/* Zoho Sign connection card */}
      <div data-testid="zoho-connect-card" className={`mb-6 rounded-sm border p-5 ${zoho.configured ? "border-[#2FB999]/40 bg-[#2FB999]/[0.05]" : "border-[#B87333]/40 bg-[#B87333]/[0.06]"}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="overline" style={{ color: zoho.configured ? "#2FB999" : "#B87333" }}>Zoho eSign</div>
            <p className="font-body text-sm text-[#C9C9C2] mt-1">
              {zoho.configured
                ? "Connected. Partners can sign agreements via Zoho eSign — signed PDFs are stored automatically."
                : zoho.credentials_present
                  ? "Not connected. Click Connect to authorise Zoho once — the refresh token is stored server-side."
                  : "Zoho client credentials are missing in backend .env. Add ZOHO_CLIENT_ID and ZOHO_CLIENT_SECRET, then reconnect."}
            </p>
          </div>
          {zoho.configured ? (
            <button data-testid="zoho-disconnect-btn" onClick={disconnectZoho}
              className="rounded-full border border-white/20 text-[#C9C9C2] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#C0392B] hover:text-[#E08A7E] transition-colors">
              Disconnect
            </button>
          ) : zoho.credentials_present ? (
            <div className="flex items-center gap-2">
              <button data-testid="zoho-connect-btn" onClick={connectZoho} disabled={zohoBusy}
                className="rounded-full bg-[#F5F5F0] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">
                {zohoBusy ? "Waiting for consent…" : "Connect Zoho"}
              </button>
              <button data-testid="zoho-paste-toggle" onClick={() => setShowPaste((v) => !v)}
                className="rounded-full border border-white/20 text-[#C9C9C2] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">
                Paste code
              </button>
            </div>
          ) : null}
        </div>

        {showPaste && !zoho.configured && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="font-body text-xs text-[#8A8A82] mb-2">
              For "Self Client" Zoho apps: in the Zoho console open <em>Self Client → Generate Code</em>, use scope
              <code className="text-[#B87333]"> ZohoSign.documents.CREATE,ZohoSign.documents.READ,ZohoSign.documents.UPDATE </code>
              and paste the resulting code here.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input data-testid="zoho-paste-input" value={pasteCode} onChange={(e) => setPasteCode(e.target.value)}
                placeholder="Paste authorization code"
                className="flex-1 min-w-[240px] bg-transparent border-b border-white/20 focus:border-[#B87333] outline-none py-2 font-body text-sm tracking-wider" />
              <button data-testid="zoho-paste-submit" onClick={pasteAuthCode} disabled={zohoBusy || !pasteCode.trim()}
                className="rounded-full bg-[#B87333] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity disabled:opacity-50">
                Exchange code
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {["all", "verified", "failed"].map((f) => (
          <button key={f} data-testid={`kyc-filter-${f}`} onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors ${filter === f ? "bg-[#F5F5F0] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>
            {f}
          </button>
        ))}
        <button data-testid="kyc-send-reminders" onClick={sendReminders} disabled={reminding}
          className="ml-auto rounded-full border border-[#B87333]/60 text-[#B87333] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:bg-[#B87333] hover:text-black transition-colors disabled:opacity-50">
          {reminding ? "Sending…" : "Send eKYC reminders"}
        </button>
      </div>
      {loading ? <p className="font-body text-[#8A8A82]">Loading…</p>
        : items.length === 0 ? <p className="font-body text-[#8A8A82]" data-testid="no-kyc">No verifications yet.</p>
        : (
          <div className="space-y-4" data-testid="admin-kyc-list">
            {items.map((k) => (
              <div key={k.user_id} data-testid="admin-kyc-row" className="border border-white/10 rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <ShieldCheck size={18} style={{ color: colors[k.status] || "#8A8A82" }} />
                      <span className="font-serif text-2xl">{k.business_name}</span>
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border" style={{ color: colors[k.status] || "#8A8A82", borderColor: `${colors[k.status] || "#8A8A82"}55` }}>{k.status}</span>
                      {k.status === "verified" && !k.admin_ack && <span data-testid={`kyc-new-${k.user_id}`} className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#B87333] border border-[#B87333]/50 rounded-full px-2 py-0.5">New</span>}
                    </div>
                    <div className="font-body text-sm text-[#8A8A82] mt-1">{k.email} · {k.account_type} · via {k.provider}</div>
                  </div>
                  <div className="text-right font-body text-xs text-[#8A8A82]">{k.submitted_at ? new Date(k.submitted_at).toLocaleString() : ""}</div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-white/10">
                  <Cell l="PAN" v={k.ekyc?.pan || "—"} />
                  <Cell l="GSTIN" v={k.ekyc?.gstin || "—"} />
                  <Cell l="eKYC result" v={k.ekyc?.verified ? "Verified" : "Failed"} />
                </div>

                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="overline text-[#8A8A82] mb-2">eKYC checks</div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    {(k.ekyc?.checks || []).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 font-body text-xs text-[#C9C9C2]">
                        {c.ok ? <Check size={13} className="text-[#2FB999]" /> : <X size={13} className="text-[#C0392B]" />}
                        {c.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="overline text-[#8A8A82] mb-2">Signed agreements</div>
                  <div className="flex flex-wrap gap-2">
                    {(k.signatures || []).map((s) => {
                      const href = s.signature_type === "zoho" && s.reference
                        ? `${backend}/api/admin/kyc/zoho/signed/${k.user_id}/${s.reference}.pdf`
                        : `${backend}/api/kyc/agreements/${s.agreement_key}/signed.pdf`;
                      return (
                        <a key={s.agreement_key} href={href} target="_blank" rel="noreferrer"
                          data-testid={`admin-signed-${s.agreement_key}`}
                          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#C9C9C2] hover:border-[#B87333] transition-colors">
                          <Download size={12} className="text-[#B87333]" /> {s.agreement_title}
                          {s.signature_type === "zoho" && <span className="text-[#2FB999]">·Zoho</span>}
                        </a>
                      );
                    })}
                  </div>
                </div>

                {k.status === "verified" && !k.admin_ack && (
                  <button data-testid={`kyc-ack-${k.user_id}`} onClick={() => ack(k.user_id)}
                    className="mt-6 rounded-full bg-[#2FB999] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity">Mark reviewed</button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ==================== Agreements Manager ====================
const EMPTY_AG = { title: "", category: "Custom", roles: ["buyer", "seller"], body: "", active: true };

export function AgreementsManager({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // key or "new"
  const [form, setForm] = useState(EMPTY_AG);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/agreements")).data); }
    catch { toast.error("Could not load agreements."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY_AG); setEditing("new"); };
  const startEdit = (a) => { setForm({ title: a.title, category: a.category, roles: a.roles, body: a.body, active: a.active }); setEditing(a.key); };
  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required."); return; }
    if (form.roles.length === 0) { toast.error("Select at least one signer role."); return; }
    setSaving(true);
    try {
      if (editing === "new") { await api.post("/admin/agreements", form); toast.success("Agreement created."); }
      else { await api.put(`/admin/agreements/${editing}`, form); toast.success("Agreement updated."); }
      setEditing(null); load();
    } catch { toast.error("Could not save agreement."); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    if (!window.confirm("Delete this agreement? Signers will no longer be asked to sign it.")) return;
    try { await api.delete(`/admin/agreements/${key}`); toast.success("Agreement deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  const inp = "w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2.5 mt-1 font-body text-[#F5F5F0]";
  return (
    <div data-testid="admin-agreements">
      <div className="flex items-center justify-between mb-6">
        <p className="font-body text-sm text-[#8A8A82] max-w-xl">Draft agreements presented to partners at signup. Add, edit or delete — use <span className="text-[#C9C9C2]">## Heading</span> lines to create numbered clauses and tokens like <span className="text-[#C9C9C2]">[Business Name]</span>, <span className="text-[#C9C9C2]">[GSTIN]</span>, <span className="text-[#C9C9C2]">[Date]</span>.</p>
        {editing === null && (
          <button data-testid="agreement-add" onClick={startNew} className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors"><Plus size={14} /> New agreement</button>
        )}
      </div>

      {editing !== null && (
        <div data-testid="agreement-editor" className="mb-8 border border-white/10 rounded-sm p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <label className="block"><span className="overline text-[#8A8A82]">Title *</span><input data-testid="ag-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inp} /></label>
            <label className="block"><span className="overline text-[#8A8A82]">Category</span><input data-testid="ag-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inp} /></label>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <span className="overline text-[#8A8A82]">Signer roles</span>
            {["buyer", "seller"].map((r) => (
              <button key={r} type="button" data-testid={`ag-role-${r}`} onClick={() => toggleRole(r)}
                className={`rounded-full px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.16em] transition-colors ${form.roles.includes(r) ? "bg-[#B87333] text-black" : "border border-white/20 text-[#C9C9C2]"}`}>{r}</button>
            ))}
            <label className="flex items-center gap-2 ml-auto cursor-pointer">
              <input type="checkbox" data-testid="ag-active" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="accent-[#B87333] w-4 h-4" />
              <span className="font-body text-sm text-[#C9C9C2]">Active (shown to signers)</span>
            </label>
          </div>
          <label className="block mt-5"><span className="overline text-[#8A8A82]">Body *</span>
            <textarea data-testid="ag-body" rows={12} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className={`${inp} resize-y font-mono text-sm leading-relaxed`} />
          </label>
          <div className="flex items-center gap-3 mt-5">
            <button data-testid="ag-save" onClick={save} disabled={saving} className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save agreement"}</button>
            <button data-testid="ag-cancel" onClick={() => setEditing(null)} className="rounded-full border border-white/20 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-white/40 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <p className="font-body text-[#8A8A82]">Loading…</p> : (
        <div className="space-y-3" data-testid="agreements-list">
          {items.map((a) => (
            <div key={a.key} data-testid={`agreement-row-${a.key}`} className="border border-white/10 rounded-sm p-5 flex flex-wrap items-center gap-4 hover:border-white/20 transition-colors">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg">{a.title}</span>
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#B87333] border border-[#B87333]/40 rounded-full px-2 py-0.5">{a.category}</span>
                  {!a.active && <span className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#8A8A82] border border-white/20 rounded-full px-2 py-0.5">Inactive</span>}
                </div>
                <div className="font-body text-xs text-[#8A8A82] mt-1">Signers: {a.roles.join(" · ")} · v{a.version}</div>
              </div>
              <button data-testid={`agreement-edit-${a.key}`} onClick={() => startEdit(a)} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:border-[#B87333] transition-colors"><Pencil size={12} /> Edit</button>
              <button data-testid={`agreement-delete-${a.key}`} onClick={() => remove(a.key)} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] hover:border-[#C0392B] hover:text-[#C0392B] transition-colors"><Trash2 size={12} /> Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
